import type { PayoutRedirectPreview } from '@/lib/api/financial/payout-preview-api'
import { MdInfoOutline, MdWarningAmber } from 'react-icons/md'
import { formatCentsLabel } from '../scheduling-format'

interface PayoutRedirectNoticeProps {
    preview: PayoutRedirectPreview | null
}

/**
 * Troca de executante com repasse já lançado.
 *
 * O aviso fica colado no seletor de parceiro/profissional porque é ali que a
 * decisão é tomada — e o que está em jogo é para quem o dinheiro vai, não o
 * valor. Bloqueado, diz por quê; permitido, lista para onde cada repasse migra.
 */
export default function PayoutRedirectNotice({
    preview,
}: PayoutRedirectNoticeProps) {
    if (preview?.blocked) {
        return (
            <div className="flex items-start gap-3 rounded-xl border border-error/30 bg-error/5 p-4">
                <MdWarningAmber className="mt-0.5 shrink-0 text-lg text-error" />
                <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium text-error">
                        Não é possível trocar quem executa neste atendimento
                    </p>
                    <p className="text-sm leading-relaxed text-base-content/70">
                        {preview.reason}
                    </p>
                </div>
            </div>
        )
    }

    if (!preview?.redirects.length) return null

    return (
        <div className="flex flex-col gap-2 rounded-xl border border-warning/30 bg-warning/5 p-4">
            <div className="flex items-start gap-3">
                <MdInfoOutline className="mt-0.5 shrink-0 text-lg text-warning" />
                <p className="text-sm leading-relaxed text-base-content/70">
                    Ao salvar, o repasse já lançado passa a ser devido a quem
                    executa agora:
                </p>
            </div>
            <ul className="flex flex-col gap-1 pl-9">
                {preview.redirects.map(redirect => (
                    <li
                        key={redirect.payableId}
                        className="text-sm text-base-content/80"
                    >
                        {formatCentsLabel(redirect.amount)} — de{' '}
                        <strong>
                            {redirect.fromName ?? 'destinatário anterior'}
                        </strong>{' '}
                        para{' '}
                        <strong>
                            {redirect.toName ?? 'novo destinatário'}
                        </strong>
                    </li>
                ))}
            </ul>
        </div>
    )
}
